import { useEffect, useState } from "react";
import { getRejectReasonCountOverall } from "../../../maketingComponents/actioncreator";
import LineChartReusable from "../components/LineChartReusable";
import moment from "moment";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import {
  getRejectionChartDataByDate,
  getRejectionHeatMapDataByDate,
} from "../../ActionCreator";
import Loader from "../../../../../presentationals/Loader/Loader.component";
import RejectionIntelligenceTable from "./RejectionIntelligenceTable";
import RejectionHeatmapTable from "./RejectionHeatmapTable";

const formatHeatmapData = (data) => {
  const stateSet = new Set();
  const reasonMap = {};

  data.forEach((item) => {
    const { reason, state, percentage } = item;

    stateSet.add(state);

    if (!reasonMap[reason]) {
      reasonMap[reason] = {
        reason,
        states: {},
      };
    }

    reasonMap[reason].states[state] = percentage;
  });

  const states = Array.from(stateSet);

  const formatted = Object.values(reasonMap).map((item) => ({
    reason: item.reason,
    states: states.map((s) => item.states[s] || 0),
  }));

  return { states, formatted };
};

function RejectionIntelligence({ appliedDate, view }) {
  const [isLoading, setIsLoading] = useState(false);
  const [rejectionReasonData, setRejectionReasonData] = useState([]);
  console.log("rejectionReasonData", rejectionReasonData);
  const [rejectionHeatMapData, setRejectionHeatMapData] = useState([]);
  const [rejectionChartData, setRejectionChartData] = useState([]);

  const { states, formatted } = formatHeatmapData(rejectionHeatMapData);
  const [heatmapData, setHeatmapData] = useState({
    states: [],
    data: [],
  });
  const [heatmapTableData, setHeatmapTableData] = useState([]);

  const getRejectionData = () => {
    const payload = {
      fromDate: appliedDate?.from
        ? moment(appliedDate.from).format("DD-MM-YYYY")
        : "",
      toDate: appliedDate?.to
        ? moment(appliedDate.to).format("DD-MM-YYYY")
        : "",
    };
    setIsLoading(true);
    getRejectReasonCountOverall(
      payload,
      (res) => {
        setIsLoading(false);
        setRejectionReasonData(res);
      },
      (err) => {
        setIsLoading(false);
        console.log(err);
      },
    );
  };

  const getRejectionHeatMapData = () => {
    const sDate = appliedDate?.from
      ? moment(appliedDate.from).format("DD-MM-YYYY")
      : "";
    const eDate = appliedDate?.to
      ? moment(appliedDate.to).format("DD-MM-YYYY")
      : "";

    setIsLoading(true);

    getRejectionHeatMapDataByDate(sDate, eDate, (res) => {
      setIsLoading(false);

      const rawData = res?.data || [];
      setHeatmapTableData(rawData);

      const { states, formatted } = formatHeatmapData(rawData);

      setHeatmapData({
        states,
        data: formatted,
      });
    });
  };

  const getRejectionChartData = () => {
    const sDate = appliedDate?.from
      ? moment(appliedDate.from).format("DD-MM-YYYY")
      : "";
    const eDate = appliedDate?.to
      ? moment(appliedDate.to).format("DD-MM-YYYY")
      : "";

    setIsLoading(true);
    getRejectionChartDataByDate(sDate, eDate, (res) => {
      setIsLoading(false);
      setRejectionChartData(res?.data);
    });
  };

  useEffect(() => {
    getRejectionData();
    getRejectionHeatMapData();
    getRejectionChartData();
  }, [appliedDate]);

  return (
    <div className="container-fluid rejection-dashboard" >
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <div className="">
            {view === "chart" ? (
              <div className="ps-4">
                <div className="card-box">
                  <h5 className="common-heading">Top Rejection Reason</h5>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={rejectionReasonData}
                      layout="vertical"
                      margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
                    >
                      <CartesianGrid
                        strokeDasharray="1"
                        horizontal={true}
                        vertical={false}
                      />

                      <XAxis
                        type="number"
                        dataKey={"count"}
                        axisLine={{
                          stroke: "rgba(0, 0, 0,0.12)",
                          strokeWidth: 1,
                        }}
                      />

                      <YAxis
                        type="category"
                        dataKey="loanRejectReason"
                        width={window.innerWidth > 1800 ? 450 : 300}
                        axisLine={{
                          stroke: "rgba(0, 0, 0,0.12)",
                          strokeWidth: 1,
                        }}
                        tick={{ fontSize: window.innerWidth > 1800 ? 16 : 11 }}
                      />

                      <Tooltip
                        formatter={(value) => [`${value}`, "Count"]}
                        labelFormatter={(label) => label}
                      />

                      <Bar
                        dataKey="count"
                        fill="#035865"
                        radius={[0, 10, 10, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <RejectionIntelligenceTable data={rejectionReasonData} />
            )}

            <div className="row align-items-baseline px-4" style={{ marginTop: "50px" }}>
              {view === "chart" ? (
                <div className="col-md-6 ">
                  <div className="card-box">
                    <h5 className="common-heading">
                      Rejection Heatmap: Reason × State
                    </h5>

                    <div className="heatmap w-100">
                      <div className="heatmap-header">
                        <span>Rejection Reason</span>
                        {heatmapData?.states?.map((s, i) => (
                          <span key={i}>{s}</span>
                        ))}
                      </div>

                      {heatmapData?.data?.map((row, i) => (
                        <div className="heatmap-row" key={i}>
                          <span className="reason" title={row.reason}>
                            {row.reason}
                          </span>

                          {row.states.map((val, j) => (
                            <span
                              key={j}
                              className={`cell ${
                                val < 10
                                  ? "low"
                                  : val <= 20
                                    ? "medium"
                                    : val <= 50
                                      ? "high"
                                      : "danger"
                              }`}
                            >
                              {val}%
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>

                    <div className="legend">
                      <span className="legend-title">Legend</span>

                      <div className="legend-item">
                        <span className="dot green" />
                        <span className="text">&lt;10%</span>
                      </div>

                      <div className="legend-item">
                        <span className="dot orange" />
                        <span className="text">10–15%</span>
                      </div>

                      <div className="legend-item">
                        <span className="dot pink" />
                        <span className="text">15–20%</span>
                      </div>

                      <div className="legend-item">
                        <span className="dot red" />
                        <span className="text">&gt;20%</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <RejectionHeatmapTable data={heatmapTableData} />
              )}
              <div
                // className="row"
                className={`col-md-${view === "chart" ? "6" : "12"} `}
                style={{
                  marginTop: `${view === "chart" ? "0" : "50px"}`,
                  background: "#fff",
                  border: "1px solid #00000026",
                  borderRadius: "10px",
                  padding: "20px",
                  height: "100%",
                }}
              >
                <div >
                  <LineChartReusable
                    data={rejectionChartData}
                    xKey="monthName"
                    yKey="count"
                    title="Rejection Rate Trend"
                    height={236}
                  />

                  <div className="info-box">
                    Current rejection rate is 62% – slightly increased from last
                    month
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default RejectionIntelligence;
